<template>
  <div class="container">
    <header class="document-header">
      <h1 class="document-title">Terms of Service for Akad Match</h1>
      <p class="updated-date">LAST UPDATED: 18th March 2025</p>
      <div class="header-divider"></div>
    </header>

    <section class="policy-section">
      <h2 class="section-title">1. INTRODUCTION</h2>
      <p>1.1. Welcome to Akad Match, where Muslims meet.</p>
      <p>1.2. These terms govern your use of Akad Match, covering features, purchases, and content.</p>
      <p><strong>1.3. PLEASE PAY SPECIAL ATTENTION TO THESE KEY RULES:</strong></p>
      <ul class="policy-list">
        <li>Subscriptions auto-renew unless canceled (see <strong>Cancellations</strong> below)</li>
        <li>14-day purchase cancellation for UK/EU users</li>
        <li>Strict community guidelines (no harassment, hate speech, or inappropriate content)</li>
        <li>Features may be updated or discontinued</li>
        <li>We may restrict accounts for rule violations</li>
      </ul>
      <p>1.4. Read our <strong>Privacy Policy</strong> alongside these Terms.</p>
      <p>1.5. Additional rules may apply to certain features—we'll notify you before use.</p>
      <p>1.6. <strong>US users:</strong> See <strong>Additional US Terms</strong> at the end.</p>
    </section>

    <section class="policy-section">
      <h2 class="section-title">2. ABOUT US</h2>
      <p>Akad Match is provided by <strong>USMANHEART OÜ</strong> (Estonia, reg. no. 16539474).</p>
    </section>

    <section class="policy-section">
      <h2 class="section-title">3. CONTACT US</h2>
      <p><strong>Email:</strong> <a href="mailto:cs@usmanheart.org" class="official-link">cs@usmanheart.org</a></p>
      <p><strong>EU users may:</strong></p>
      <ul class="policy-list">
        <li>Use third-party dispute resolution</li>
        <li>File complaints with local regulators</li>
      </ul>
    </section>

    <section class="policy-section">
      <h2 class="section-title">4. ACCOUNT & ACCESS</h2>
      <p><strong>4.1. Requirements:</strong></p>
      <ul class="policy-list">
        <li>Must be 18+</li>
        <li>No history of violent/sexual crimes</li>
        <li>Valid email/phone verification</li>
      </ul>
      <p><strong>4.2. We may restrict accounts for:</strong></p>
      <ul class="policy-list">
        <li>False information</li>
        <li>Harmful behavior</li>
        <li>Technical misuse</li>
      </ul>
    </section>

    <section class="policy-section">
      <h2 class="section-title">5. PURCHASES & SUBSCRIPTIONS</h2>
      <p>5.1. Prices include taxes. Cancel anytime via:</p>
      <ul class="policy-list">
        <li>App Store/Google Play settings</li>
        <li>Stripe dashboard</li>
      </ul>
      <p>5.2. Subscriptions auto-renew. We'll notify you before charging.</p>
    </section>

    <section class="policy-section">
      <h2 class="section-title">6. CONTENT RULES</h2>
      <p><strong>6.1. You agree NOT to post:</strong></p>
      <ul class="policy-list">
        <li>Offensive/abusive material</li>
        <li>Fake profiles or impersonation</li>
        <li>Spam or illegal content</li>
        <li>Private info of others</li>
      </ul>
      <p>6.2. We may remove content violating these rules.</p>
    </section>

    <section class="policy-section">
      <h2 class="section-title">7. LIMITATIONS</h2>
      <p><strong>7.1. We’re not liable for:</strong></p>
      <ul class="policy-list">
        <li>User-to-user interactions</li>
        <li>Technical issues beyond our control</li>
        <li>Content accuracy</li>
      </ul>
      <p>7.2. Maximum liability: £100 or 12-month subscription fee (whichever is higher).</p>
    </section>

    <section class="policy-section">
      <h2 class="section-title">8. CHANGES & TERMINATION</h2>
      <p>8.1. We may update these Terms. Continued use = acceptance.</p>
      <p>8.2. Close your account via app settings or email request.</p>
    </section>

    <section class="policy-section">
      <h2 class="section-title">9. LEGAL BASICS</h2>
      <ul class="policy-list">
        <li><strong>Governing law:</strong> Estonia</li>
        <li><strong>Disputes resolved through</strong> negotiation or courts</li>
        <li><strong>US users:</strong> See arbitration terms below</li>
      </ul>
    </section>

    <section class="policy-section">
      <h2 class="section-title">Additional US Terms</h2>
      <ul class="policy-list">
        <li>Disputes resolved via arbitration in New York</li>
        <li>No class actions permitted</li>
        <li>Opt-out instructions available</li>
      </ul>
    </section>

    <footer class="document-footer">
      <h3 class="footer-title">Contact</h3>
      <dl class="contact-info">
        <dt>Email:</dt>
        <dd><a href="mailto:cs@usmanheart.org" class="official-link">cs@usmanheart.org</a></dd>
      </dl>
      <p><strong>Company:</strong> USMANHEART OÜ</p>
    </footer>
  </div>
</template>

<script>
export default {
  name: "TermsOfService"
};
</script>

<style scoped>
.container {
  max-width: 850px;
  margin: 2rem auto;
  padding: 2.5rem;
  background: #ffffff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  font-family: 'Segoe UI', system-ui, sans-serif;
  line-height: 1.6;
}

.document-header {
  margin-bottom: 2rem;
  border-bottom: 3px solid #2c3e50;
}

.document-title {
  color: #2c3e50;
  font-size: 2.2rem;
  margin-bottom: 0.5rem;
}

.updated-date {
  color: #7f8c8d;
  font-size: 1rem;
}

.section-title {
  color: #34495e;
  font-size: 1.6rem;
  margin: 1.5rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #ecf0f1;
}

.policy-list {
  padding-left: 1.8rem;
}

.policy-item {
  margin: 1rem 0;
  padding: 0.8rem;
  background: #f8f9fa;
  border-left: 4px solid #3498db;
  border-radius: 4px;
}

.official-link {
  color: #2980b9;
  text-decoration: none;
  font-weight: 500;
}

.official-link:hover {
  color: #3498db;
  text-decoration: underline;
}

.document-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #ecf0f1;
  text-align: center;
}
</style>
